import React, { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { Search } from "react-feather";
import { motion, AnimatePresence } from "framer-motion";

// React-Bootstrap Integration
import {
  Col,
  Row,
  Form,
  Button,
  InputGroup,
  Modal,
  Table,
  Spinner
} from "react-bootstrap";

// Custom API and Asset Dependencies Maintained
import { searchInvoices } from "../../../Data/Customer/api";
import InvoiceTable from "./NavLeft/InvoiceTable";
import "./styles/style.css";

const NavLeft = (props) => {
  const navigate = useNavigate();
  const auth = useSelector((state) => state.authentication);
  const { userData } = auth;
  
  // --- Core State Variables ---
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState([]); 
  const [modal, setModal] = useState(false);
  const [selectedCM, setSelectedCM] = useState("");
  const [otData, setOTData] = useState("");
  const [modalName, setModalName] = useState("search");
  const [paymentHistory, setPaymentHistory] = useState("");
  const [loadingModal, setLoadingModal] = useState(false);

  const toggleLoadingModal = () => setLoadingModal(!loadingModal);
  const toggle = () => setModal(!modal);

  const branch = sessionStorage.getItem("branch")
    ? JSON.parse(sessionStorage.getItem("branch"))
    : props.branch;

  // --- Initial Synchronizations ---
  useEffect(() => {
    const cachedData = sessionStorage.getItem("srchData");
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        setSelectedCM(parsed);
        console.log("OT Data ", parsed);
        setOTData(parsed);
      } catch (err) {
        console.error("Error parsing srchData from sessionStorage", err);
      }
    }
  }, []);

  // --- Strict Customer Search Request Logic ---
  const searchHandler = (inputVal) => {
    if (!inputVal) return;
    axios
      .get(`${process.env.REACT_APP_CRI_API}searchcustomers`, {
        params: {
          branch: branch,
          search: inputVal,
        },
        headers: {
          Authorization: `Bearer ${props.token}`,
        },
      })
      .then((res) => {
        setSearchResults(res.data || []);
        if (props.customerDataHandler) {
          props.customerDataHandler(res.data);
        }
        if (props.mrn && res.data) {
          const temp = res.data.filter((item) => item.mrn === props.mrn);
          if (temp.length > 0) setOTData(temp[0]);
        }
      })
      .catch((err) => {
        if (
          (err.response?.data &&
            err.response.data?.err?.name === "TokenExpiredError") ||
          err.response?.data?.message === "Unauthorized"
        ) {
          sessionStorage.clear(); 
          navigate("/cri-crm");
          return err;
        }
      });
  };

  useEffect(() => {
    if (props.mrn && searchResults?.length) {
      const temp = searchResults.filter((item) => item.mrn === props.mrn);
      if (temp.length > 0) setOTData(temp[0]);
    }
  }, [props.mrn, searchResults]);

  useEffect(() => {
    const mobileNo = props.contactno;
    if (mobileNo && mobileNo.length > 9 && !props.mrn) {
      searchHandler(mobileNo);
      setModalName("search");
      setModal(true);
    }
  }, [props.contactno]);

  useEffect(() => {
    if (selectedCM) {
      if (props.searchUpdateHandler) {
        props.searchUpdateHandler(selectedCM);
      }
      setOTData(selectedCM);
    }
  }, [selectedCM]);

  // --- Clinical Invoicing Workflows ---
  const getInvoicesHandler = () => {
    if (!selectedCM || !selectedCM.mrn) {
      alert("Please select a patient first.");
      return;
    }
    
    setLoadingModal(true);
    searchInvoices(
      userData?.token,
      selectedCM.mrn,
      userData?.branch,
      navigate
    )
      .then((res) => {
        setPaymentHistory(res);
        setModalName("Payment History");
        setModal(true);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoadingModal(false));
  };

  return (
    <Col md={2} className="l-nav-bg p-3 shadow-sm min-vh-100">
      <div className="d-flex flex-column gap-2">
        
        {/* Modernized Search Header Segment */}
        <InputGroup className="mb-3">
          <Form.Control
            placeholder="Search patient..."
            className="border-end-0 bg-white shadow-none"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && searchInput) {
                searchHandler(searchInput);
                setModalName("search");
                setModal(true);
              }
            }}
          />
          <InputGroup.Text 
            className="border-start-0 text-white"
            style={{ background: "rgba(50, 0, 100, 0.7)", cursor: "pointer" }}
            onClick={() => {
              if (searchInput) {
                searchHandler(searchInput);
                setModalName("search");
                setModal(true);
              }
            }}
          >
            <Search size={16} />
          </InputGroup.Text>
        </InputGroup>

        {/* Action Controls Navigation Matrix */}
        <div className="d-flex flex-column gap-2 w-100">
         <button
            className="glass-button text-start px-3 py-2 btn btn-link text-decoration-none"
            onClick={() => {
              if (otData && otData.mrn) {
                const { _id, ...cleanOtData } = otData;
                const targetPath = props.branch === "Carmel" ? "/billentry-wt" : "/billentry";
                navigate(targetPath, {
                  state: { otData: cleanOtData, token: props.token, customerData: props.customerData, branch: props.branch },
                });
              } else {
                alert("please select a customer");
              }
            }}
          >
            Generate Invoice
          </button>

          <button
            className="glass-button text-start px-3 py-2 btn btn-link text-decoration-none"
            onClick={() => {
              if (otData && otData.mrn) {
                const { _id, ...cleanOtData } = otData;
                navigate("/bill-tabs", {
                  state: { otData: cleanOtData, token: props.token, customerData: props.customerData, branch: props.branch },
                });
              } else {
                alert("please select a customer");
              }
            }}
          >
            Generate Invoice (New Tab)
          </button>

          <button className="glass-button text-start px-3 py-2 btn btn-link text-decoration-none" onClick={() => navigate("/invoices")}>
            View Invoices
          </button>

          <button className="glass-button text-start px-3 py-2 btn btn-link text-decoration-none" onClick={() => navigate("/rtn-invoices")}>
            View Return Invoices
          </button>

          <button className="glass-button text-start px-3 py-2 btn btn-link text-decoration-none" onClick={getInvoicesHandler}>
            Patient Invoice History
          </button>

          <button
            className="glass-button text-start px-3 py-2 btn btn-link text-decoration-none"
            onClick={() => {
              navigate("/otBooking", {
                state: { otData, token: props.token, customerData: props.customerData, doctorsList: props.doctorsList, branch: props.branch },
              });
            }}
          >
            Appointments
          </button>

          <button
            className="glass-button text-start px-3 py-2 btn btn-link text-decoration-none"
            onClick={() => {
              navigate("/phmcy-admin", {
                state: { otData, token: props.token, customerData: props.customerData, branch: props.branch },
              });
            }}
          >
            Pharmacy
          </button>

          <button
            className="glass-button text-start px-3 py-2 btn btn-link text-decoration-none"
            onClick={() => {
              navigate("/lab-admin", {
                state: { otData, token: props.token, customerData: props.customerData, branch: props.branch },
              });
            }}
          >
            Lab
          </button>

          <button
            className="glass-button text-start px-3 py-2 btn btn-link text-decoration-none"
            onClick={() => {
              navigate("/ccare", {
                state: { otData, token: props.token, customerData: props.customerData, branch: props.branch },
              });
            }}
          >
            Patient Care
          </button>

          <button
            className="glass-button text-start px-3 py-2 btn btn-link text-decoration-none"
            onClick={() => {
              navigate("/customer_form", {
                state: { otData, token: props.token, customerData: props.customerData, branch: props.branch },
              });
            }}
          >
            Patient Form
          </button>

          <button
            className="glass-button text-start px-3 py-2 btn btn-link text-decoration-none"
            onClick={() =>
              navigate("/reports", {
                replace: true,
                state: { branch: props.branch, token: props.token },
              })
            }
          >
            Daily Reports
          </button>

          <button
            className="glass-button text-start px-3 py-2 btn btn-link text-decoration-none"
            onClick={() =>
              navigate("/queue-management", {
                state: { branch: props.branch, token: props.token },
              })
            }
          >
            Queue Management
          </button>
        </div>
      </div>

      {/* --- Overlay Modals Infrastructure --- */}
      <AnimatePresence mode="wait">
        {modal && (
          <Modal show={modal} onHide={toggle} size="xl" centered scrollable>
            <Modal.Header closeButton className="bg-light">
              <Modal.Title className="fs-5 fw-bold text-dark">
                {modalName === "search" ? "Search Results" : modalName}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-3">
              {modalName === "search" && (
                <Table responsive hover striped className="align-middle border mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Patient Name</th>
                      <th>Contact No</th>
                      <th>Address</th>
                      <th>Treatment Type</th>
                      <th>MRN</th>
                      <th className="text-center">Select</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults && searchResults.length > 0 ? (
                      searchResults.map((cm, id) => (
                        <tr key={id}>
                          <td className="fw-medium">{cm.fname} {cm.sname}</td>
                          <td>{cm.contactno}</td>
                          <td><div className="text-truncate" style={{ maxWidth: "200px" }}>{cm.address}</div></td>
                          <td>{cm.treatmenttype}</td>
                          <td className="text-monospace small">{cm.mrn}</td>
                          <td className="text-center">
                            <Form.Check
                              type="radio"
                              name="select-patient"
                              id={`patient-radio-${id}`}
                              checked={selectedCM?.mrn === cm?.mrn}
                              onChange={() => {
                                setSelectedCM(cm);
                                sessionStorage.setItem("srchData", JSON.stringify(cm));
                              }}
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-4">
                          {!paymentHistory ? <Spinner animation="border" size="sm" /> : "No records found"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}

              {modalName === "otBooking" && (
                <Row>
                  <Col></Col>
                  <Col></Col>
                </Row>
              )}

              {modalName === "Payment History" && paymentHistory && (
                <InvoiceTable
                  invoices={paymentHistory}
                  getInvoicesHandler={getInvoicesHandler}
                  selectedCM={selectedCM}
                />
              )}
            </Modal.Body>
            <Modal.Footer className="bg-light py-2">
              <Button variant="primary" size="sm" onClick={toggle}>Ok</Button>
              <Button variant="secondary" size="sm" onClick={toggle}>Cancel</Button>
            </Modal.Footer>
          </Modal>
        )}
      </AnimatePresence>

      {/* Synchronous Loading State Overlay Indicator */}
      <Modal show={loadingModal} onHide={toggleLoadingModal} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">System Processing</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <Spinner animation="grow" color="primary" className="mb-3" style={{ width: "3rem", height: "3rem" }} />
          <p className="text-muted small mb-0">Synchronizing secure diagnostic streams. Please wait...</p>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button variant="outline-secondary" size="sm" onClick={toggleLoadingModal}>Close</Button>
        </Modal.Footer>
      </Modal>
    </Col>
  );
};

export default NavLeft;